import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@sea-snag/ui';
import { type ReactNode } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

export function LandingCompetitorSets() {
  return (
    <section id="competitor-sets" className="scroll-mt-20 border-t border-border py-32 md:py-40">
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-12 max-w-3xl">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Competitor sets
          </p>
          <h2 className="mb-6 text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Market averages don&rsquo;t move your prices. The right competitors do.
          </h2>
          <p className="mb-4 text-base text-muted-foreground md:text-lg">
            Build precise competitive sets from any combination of yacht class, model, length, base,
            charter type, and operator. Refine them as the season evolves. Sea Snag tracks the exact
            set you choose, not a one-size-fits-all index.
          </p>
          <p className="text-base text-muted-foreground md:text-lg">
            Every set comes with its own analytics: median pricing, spread, occupancy, and
            week-over-week movement, with trend lines stretching back across seasons. Slice by week,
            by base, by charter type, and see exactly how your fleet stacks up against the
            competitors you actually compete with.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-background p-6 space-y-6">
          {/* Filter chip rail */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Active set
            </span>
            <ChipBadge>Bavaria · 45-50ft</ChipBadge>
            <ChipBadge>Split base</ChipBadge>
            <ChipBadge>Bareboat</ChipBadge>
            <ChipBadge>Week of Jul 6</ChipBadge>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border bg-border md:grid-cols-4">
            <StatCell
              label="Set median"
              value="€4,710"
              detail="Jul 6 week"
              delta="+2.1%"
              tone="up"
            />
            <StatCell label="Spread" value="€3,400 to €8,400" detail="5 boats" />
            <StatCell
              label="Occupancy"
              value="67%"
              detail="Jul"
              delta="+9 pts vs last yr"
              tone="up"
            />
            <StatCell
              label="Week-over-week"
              value="−1.4%"
              detail="median"
              delta="set softening"
              tone="down"
            />
          </div>

          {/* Trend chart */}
          <SetMedianTrendChart />

          {/* Per-boat detail table */}
          <CompetitorWatchMock />
        </div>
      </div>
    </section>
  );
}

function ChipBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full border border-border bg-muted/30 px-2.5 py-1 font-mono text-[11px] text-foreground">
      {children}
    </span>
  );
}

function StatCell({
  label,
  value,
  detail,
  delta,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  delta?: string;
  tone?: 'up' | 'down';
}) {
  return (
    <div className="bg-background p-4">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-mono text-xl tabular-nums font-semibold text-foreground">{value}</p>
      {detail && (
        <p className="mt-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">{detail}</p>
      )}
      {delta && (
        <p
          className={
            tone === 'down'
              ? 'mt-2 font-mono text-[11px] tabular-nums text-rose-600 dark:text-rose-400'
              : tone === 'up'
                ? 'mt-2 font-mono text-[11px] tabular-nums text-emerald-600 dark:text-emerald-400'
                : 'mt-2 font-mono text-[11px] tabular-nums text-muted-foreground'
          }
        >
          {delta}
        </p>
      )}
    </div>
  );
}

const TREND_DATA = [
  { week: 'May 04', median: 3120 },
  { week: 'May 11', median: 3280 },
  { week: 'May 18', median: 3450 },
  { week: 'May 25', median: 3620 },
  { week: 'Jun 01', median: 3890 },
  { week: 'Jun 08', median: 4150 },
  { week: 'Jun 15', median: 4380 },
  { week: 'Jun 22', median: 4520 },
  { week: 'Jun 29', median: 4690 },
  { week: 'Jul 06', median: 4710 },
  { week: 'Jul 13', median: 4640 },
  { week: 'Jul 20', median: 4480 },
];

const TREND_CONFIG = {
  median: {
    label: 'Set median',
    color: 'oklch(0.55 0.22 265)',
  },
} satisfies ChartConfig;

function SetMedianTrendChart() {
  return (
    <div className="rounded-md border border-border bg-background p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Set median · last 12 weeks
        </p>
        <p className="font-mono text-[11px] tabular-nums text-muted-foreground">EUR / week</p>
      </div>
      <ChartContainer config={TREND_CONFIG} className="aspect-[16/4] w-full">
        <AreaChart data={TREND_DATA} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="setMedianGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-median)" stopOpacity={0.18} />
              <stop offset="100%" stopColor="var(--color-median)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis
            dataKey="week"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            interval="preserveStartEnd"
            fontSize={11}
            stroke="var(--muted-foreground)"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value: number) => `€${(value / 1000).toFixed(1)}k`}
            domain={['dataMin - 200', 'dataMax + 200']}
            fontSize={11}
            stroke="var(--muted-foreground)"
            width={48}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                indicator="line"
                formatter={(value) => `€${Number(value).toLocaleString()}`}
              />
            }
          />
          <Area
            type="monotone"
            dataKey="median"
            stroke="var(--color-median)"
            strokeWidth={2}
            fill="url(#setMedianGrad)"
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
}

function CompetitorWatchMock() {
  const rows = [
    {
      boat: 'Bavaria 46 Cruiser · "Adriatic Wind"',
      price: '€3,940',
      delta: '−4.2%',
      last: '2h ago',
    },
    { boat: 'Sun Odyssey 410 · "Lavanda"', price: '€3,520', delta: '+1.8%', last: '6h ago' },
    { boat: 'Oceanis 51.1 · "Mistral"', price: '€6,120', delta: '−2.5%', last: '1d ago' },
    { boat: 'Lagoon 42 · "Gemini"', price: '€8,400', delta: '+0.0%', last: '3d ago' },
    { boat: 'Dufour 470 · "Borealis"', price: '€4,710', delta: '−6.1%', last: '4h ago' },
  ];
  return (
    <div className="overflow-hidden rounded-md border border-border">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-3 font-medium">Boat</th>
            <th className="px-4 py-3 font-medium font-mono">Week of Jul 6</th>
            <th className="px-4 py-3 font-medium">Last move</th>
            <th className="px-4 py-3 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr key={row.boat} className="hover:bg-muted/20">
              <td className="px-4 py-3 text-foreground">{row.boat}</td>
              <td className="px-4 py-3 font-mono tabular-nums text-foreground">{row.price}</td>
              <td
                className={
                  row.delta.startsWith('−')
                    ? 'px-4 py-3 font-mono tabular-nums text-rose-600 dark:text-rose-400'
                    : row.delta.startsWith('+0')
                      ? 'px-4 py-3 font-mono tabular-nums text-muted-foreground'
                      : 'px-4 py-3 font-mono tabular-nums text-emerald-600 dark:text-emerald-400'
                }
              >
                {row.delta}
              </td>
              <td className="px-4 py-3 text-muted-foreground">{row.last}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
